import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleMinus,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";

import type { ChecklistItemStatus, ChecklistSchemaItem } from "@/services/api/agentic-v2";
import type { ChecklistRating } from "@/services/api/agentic-v2/types";

export const DEFAULT_HUMAN_REVIEW_LABEL = "Needs review";

/** Overall DG rating — label, help text, and colours (Badge / StatusDot names). */
export const CHECKLIST_RATING_META: Record<
  ChecklistRating,
  { label: string; description: string; color: "emerald" | "amber" | "red" }
> = {
  green: {
    label: "Good to go",
    description: "Everything needed for the narrative is in place.",
    color: "emerald",
  },
  amber: {
    label: "Nearly there",
    description: "Can proceed — some items are only adequate or still open.",
    color: "amber",
  },
  red: {
    label: "Blocked",
    description: "Something needed for the narrative is still partial or missing.",
    color: "red",
  },
};

/** True when a hard-stop item is still pending or missing. */
export function isOpenHardStop(item: { hard_stop?: boolean; status: ChecklistItemStatus }): boolean {
  return Boolean(item.hard_stop) && (item.status === "pending" || item.status === "missing");
}

/** Status values agents and humans set on populated checklist artefacts. */
export const CHECKLIST_ITEM_STATUSES: ChecklistItemStatus[] = [
  "pending",
  "complete",
  "adequate",
  "missing",
  "not_required",
];

/** Plain-language meaning of each status (for schema editors and help text). */
export const CHECKLIST_STATUS_HELP: Record<
  ChecklistItemStatus,
  { label: string; description: string }
> = {
  pending: {
    label: "Pending",
    description: "Not assessed yet, or the agent has not filled this in.",
  },
  complete: {
    label: "Complete",
    description: "Requirement met — usually with a short evidence note.",
  },
  adequate: {
    label: "Adequate",
    description:
      "Good enough for narrative — gaps covered by Aveni, Plannr notes, or adviser commentary.",
  },
  missing: {
    label: "Missing",
    description: "Required information was not found in sources.",
  },
  not_required: {
    label: "Not required",
    description: "Does not apply to this client or case.",
  },
};

/** Badge text for items that require a human sign-off (tenant-overridable). */
export function humanReviewBadgeLabel(
  item: Pick<ChecklistSchemaItem, "requires_human_confirmation" | "review_label">,
): string | null {
  if (!item.requires_human_confirmation) return null;
  const custom = item.review_label?.trim();
  return custom || DEFAULT_HUMAN_REVIEW_LABEL;
}

export interface ChecklistStatusVisual {
  label: string;
  icon: IconDefinition;
  iconClass: string;
  rowClass: string;
}

const STATUS_VISUALS: Record<ChecklistItemStatus, ChecklistStatusVisual> = {
  complete: {
    label: "Complete",
    icon: faCircleCheck,
    iconClass: "text-emerald-400",
    rowClass: "border-emerald-500/20",
  },
  adequate: {
    label: "Adequate",
    icon: faCircleCheck,
    iconClass: "text-teal-400",
    rowClass: "border-teal-500/25",
  },
  missing: {
    label: "Missing",
    icon: faCircleExclamation,
    iconClass: "text-rose-400",
    rowClass: "border-rose-500/25",
  },
  not_required: {
    label: "Not required",
    icon: faCircleMinus,
    iconClass: "text-slate-500",
    rowClass: "border-slate-700/60",
  },
  pending: {
    label: "Pending",
    icon: faCircle,
    iconClass: "text-slate-500",
    rowClass: "border-slate-700/80",
  },
};

export function checklistStatusVisual(status: ChecklistItemStatus): ChecklistStatusVisual {
  return STATUS_VISUALS[status] ?? STATUS_VISUALS.pending;
}

export function formatChecklistStatus(status: ChecklistItemStatus): string {
  return checklistStatusVisual(status).label;
}

export function sectionProgress(items: Array<{ status: ChecklistItemStatus }>): {
  done: number;
  total: number;
  pct: number;
} {
  const total = items.length;
  if (total === 0) return { done: 0, total: 0, pct: 0 };
  const done = items.filter(
    (item) =>
      item.status === "complete" ||
      item.status === "adequate" ||
      item.status === "not_required",
  ).length;
  return { done, total, pct: Math.round((100 * done) / total) };
}
